import { useRef, useCallback, useState } from 'react';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { useI18n } from '../i18n';

export interface SettingToggle {
  key: string;
  labelOn: string;
  labelOff: string;
  value: boolean;
  onToggle: () => void;
  icon?: string;
  helpText?: string;
}

interface SettingsPopupProps {
  settings: SettingToggle[];
  isOpen: boolean;
  onToggle: () => void;
  buttonLabel: string;
}

export function SettingsPopup({ settings, isOpen, onToggle, buttonLabel }: SettingsPopupProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [openHelpKey, setOpenHelpKey] = useState<string | null>(null);
  const handleClose = useCallback(() => { if (isOpen) onToggle(); }, [isOpen, onToggle]);
  useOutsideClick(ref, isOpen, handleClose);

  return (
    <div className="header-settings-wrap" ref={ref}>
      <button
        className={`global-ctrl-btn header-settings-btn icon-btn${isOpen ? ' active' : ''}`}
        onClick={onToggle}
        title={buttonLabel}
        aria-label={buttonLabel}
      >
        {/* Dişli çark — metin yok */}
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>
      {isOpen && (
        <div className="header-settings-popup">
          {settings.map(s => (
            <div key={s.key} className="header-settings-row">
              <button
                className={`header-settings-item ${s.value ? 'active' : ''}`}
                onClick={s.onToggle}
              >
                {s.icon && <span>{s.icon}</span>} {s.value ? s.labelOn : s.labelOff}
              </button>
              {s.helpText && (
                <button
                  type="button"
                  className={`header-settings-help-btn${openHelpKey === s.key ? ' active' : ''}`}
                  onClick={() => setOpenHelpKey(key => key === s.key ? null : s.key)}
                  aria-label={t.common.info}
                  title={t.common.info}
                >
                  ?
                </button>
              )}
              {s.helpText && openHelpKey === s.key && (
                <div className="header-settings-help-text" role="status">
                  {s.helpText}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
